import { Container, SpreadDisplay, Wrapper } from './styles';

import { getPriceDiff, getSpreadPctDisplay, parseColor, SpreadUnit } from '@talos/kyoko';
import { useTheme } from 'styled-components';

const MIN_VALUE = -5;
const MAX_VALUE = 5;

export const TradeIndicator = ({ scale = 1, fill = 'gray.080', ...props }) => {
  const theme = useTheme();
  const fillColor = parseColor(theme, fill);
  return (
    <svg viewBox="0 0 25 19" style={{ transform: `scale(${scale})` }} width={21} height={16} {...props}>
      <g fill={fillColor} fillRule="evenodd" stroke="transparent">
        <polygon points="6.83152287 5.29576143 12.1272843 8.8817842e-16 17.4230457 5.29576143 12.1272843 10.5915229" />
        <polygon points="0 12.1260245 5.29576143 6.83026311 10.5915229 12.1260245 5.29576143 17.421786" />
        <polygon points="13.6630457 12.1260245 18.9588072 6.83026311 24.2545686 12.1260245 18.9588072 17.421786" />
      </g>
    </svg>
  );
};

export function TradeDirection({
  direction,
  bid,
  offer,
  differenceUnit = SpreadUnit.BPS,
  priceUnit,
  showShadow = false,
  isPricingModeBPS = false,
}: {
  direction: number;
  bid: string | undefined;
  offer: string | undefined;
  differenceUnit?: SpreadUnit;
  priceUnit?: string;
  showShadow?: boolean;
  isPricingModeBPS?: boolean;
}) {
  const theme = useTheme();
  const getLogScale = (direction: number) => {
    const absDirection = Math.abs(direction);
    const logVal = Math.log10(absDirection * 3); // get value between 0-1
    return direction > 0 ? logVal * 1.5 : logVal * -1.5;
  };

  if (!bid || !offer) {
    return null;
  }

  // When in BPS pricing mode, prices are already in absolute BPS terms, so use getPriceDiff for absolute difference
  const spreadDisplay =
    differenceUnit === SpreadUnit.BPS && isPricingModeBPS ? getPriceDiff(bid, offer) : getSpreadPctDisplay(bid, offer);
  const priceDisplay = getPriceDiff(bid, offer);

  const scale = getLogScale(direction);
  const fill = direction > 0 ? theme.colors.green.lighten : theme.colors.red.lighten;

  return (
    <Wrapper>
      {differenceUnit === SpreadUnit.BPS && (
        /* don't show BPS on Hover if the value is a percentage */
        <SpreadDisplay unit={isPricingModeBPS || spreadDisplay?.slice(-1) !== '%' ? 'BPS' : undefined}>
          <p data-testid="top-of-book-spread-display">{spreadDisplay}</p>
        </SpreadDisplay>
      )}
      {differenceUnit === SpreadUnit.PRICE && (
        <SpreadDisplay unit={priceUnit}>
          <p data-testid="top-of-book-spread-display">{priceDisplay}</p>
        </SpreadDisplay>
      )}
      {direction !== 0 && direction >= MIN_VALUE && direction <= MAX_VALUE && (
        <Container direction={direction} showShadow={showShadow && direction <= 0}>
          <TradeIndicator scale={scale} fill={fill} />
        </Container>
      )}
    </Wrapper>
  );
}

TradeDirection.defaultProps = {
  direction: 0,
};
